import {FormControl, TextInput} from '@primer/react'
import {DataTable, Table} from '@primer/react/experimental'
import {useEffect, useMemo, useState} from 'react'

import {TraceDetails} from './TraceDetails'
import styles from './TraceList.module.css'
import type {IDNode} from './utilities/tree-utilities'

export function TraceList({
  treeLists,
  verboseMode,
}: {
  treeLists: {fullList: IDNode[]; shortList: IDNode[]}
  verboseMode: boolean
}) {
  const [activeNode, setActiveNode] = useState<IDNode | null>(null)
  const [filter, setFilter] = useState('')

  const {fullList, shortList} = treeLists

  const filteredList = useMemo(() => {
    const lcFilter = filter.toLowerCase()
    const currentList = verboseMode ? fullList : shortList
    return currentList.filter(node => searchNode(node, lcFilter))
  }, [fullList, shortList, filter, verboseMode])

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement
    const row = target.closest('tr')
    if (!row) return
    const idColumn = row.querySelector('td:first-child')
    if (!idColumn) return
    const spanId = idColumn.textContent?.trim()
    if (!spanId) return

    // Find node in the currently visible list
    const currentList = filter ? filteredList : verboseMode ? fullList : shortList
    const node = currentList.find(n => n.id === spanId)
    if (node) {
      setActiveNode(node)
    }
  }

  useEffect(() => {
    // drop any previous highlighted row
    const allRows = document.querySelectorAll('tr[data-active="true"]')
    for (const row of allRows) {
      row.removeAttribute('data-active')
    }

    // Set active attribute on the current row if we have an activeNode
    if (activeNode) {
      const allTables = document.querySelectorAll('.traceTable')
      for (const table of allTables) {
        const rows = table.querySelectorAll('tbody tr')
        for (const row of rows) {
          const idCell = row.querySelector('td:first-child')
          if (idCell?.textContent?.trim() === activeNode.id) {
            row.setAttribute('data-active', 'true')
            break
          }
        }
      }
    }
  }, [activeNode])

  const tableColumns = [
    {header: 'Span ID', field: 'id' as keyof IDNode, sortBy: 'alphanumeric' as const},
    {header: 'Name', field: 'name' as keyof IDNode, sortBy: 'alphanumeric' as const},
    {header: 'Start Time', field: 'startTime' as keyof IDNode, sortBy: 'alphanumeric' as const},
    {header: 'End Time', field: 'endTime' as keyof IDNode, sortBy: 'alphanumeric' as const},
    {header: 'Duration', field: 'duration' as keyof IDNode, sortBy: 'alphanumeric' as const},
    {
      header: 'Has LLM Data?',
      field: 'hasData' as keyof IDNode,
      sortBy: 'basic' as const,
    },
  ]

  return (
    <div className={styles.traceList}>
      <FormControl>
        <FormControl.Label>Search: </FormControl.Label>
        <TextInput type="text" value={filter} onChange={e => setFilter(e.target.value)} />
      </FormControl>

      {/* Verbose mode table - always rendered, visibility controlled by CSS */}
      {treeLists.fullList.length > 0 && (
        <Table.Container
          className={`${styles.traceTable} traceTable`}
          onClick={handleClick}
          style={{display: verboseMode ? 'block' : 'none'}}
        >
          <Table.Title id="trace-table-verbose">Verbose trace table</Table.Title>
          <DataTable
            data={filter ? filteredList : treeLists.fullList}
            columns={tableColumns}
            initialSortColumn={'startTime'}
            initialSortDirection="DESC"
          />
        </Table.Container>
      )}

      {/* Filtered mode table - always rendered, visibility controlled by CSS */}
      {treeLists.shortList.length > 0 ? (
        <Table.Container
          className={`${styles.traceTable} traceTable`}
          onClick={handleClick}
          style={{display: !verboseMode ? 'block' : 'none'}}
        >
          <Table.Title id="trace-table-filtered">Filtered trace table</Table.Title>
          <DataTable
            data={filter ? filteredList : treeLists.shortList}
            columns={tableColumns}
            initialSortColumn={'startTime'}
            initialSortDirection="DESC"
          />
        </Table.Container>
      ) : (
        <div style={{padding: '16px', textAlign: 'center', color: '#666'}}>
          No spans to display. Try enabling verbose mode to see all spans.
        </div>
      )}

      {activeNode && <TraceDetails node={activeNode} />}
    </div>
  )
}

function searchNode(node: IDNode, filter: string): boolean {
  return !!(
    node.id.toLowerCase().includes(filter) ||
    node.name.toLowerCase().includes(filter) ||
    node.attributes?.some(
      attr => String(attr.value).toLowerCase().includes(filter) || attr.key.toLowerCase().includes(filter),
    )
  )
}
